import React from 'react'

import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { resolveLink } from '~/components/serializers'
import type { ComponentTheme } from '~/components/themes'
import type { Block } from '~/services/sanity'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './MultiInternalPageLinks.module.css'

const PageLinkItem = ({
  pageLink,
  isLarge = true,
  whiteCard = false,
}: {
  pageLink: any
  isLarge?: boolean
  whiteCard?: boolean
}) => {
  return (
    <div
      className={classNames(styles.pageLink, {
        [styles.large]: isLarge,
        [styles.noImage]: !pageLink.image || !pageLink.image.asset,
        [styles.whiteCard]: whiteCard,
      })}
    >
      {pageLink.image && pageLink.image.asset && (
        <div className={styles.imageContainer}>
          <div className={styles.imageContainerInner}>
            {pageLink.image && pageLink.image.asset && (
              <Image
                asset={pageLink.image.asset}
                alt={`${pageLink.title} - MyArtBroker`}
              />
            )}
          </div>
          {pageLink.imageCredit && (
            <Text
              className={classNames(styles.imageCredit, {
                [styles[pageLink.imageCreditColor]]: pageLink.imageCreditColor,
              })}
              size={TextSizes.tertiaryTitleSml}
            >
              {pageLink.imageCredit}
            </Text>
          )}
        </div>
      )}
      <div className={styles.pageLinkContent}>
        <div className={styles.pageLinkTextGroup}>
          {pageLink.numberedListTitle && (
            <div className={styles.numberedListContainer}>
              <Text
                className={styles.numberedListTitle}
                size={TextSizes.primaryTitleMed}
              >
                {pageLink.numberedListTitle}
              </Text>
            </div>
          )}
          <Text
            className={styles.pageLinkTitle}
            size={TextSizes.primaryTitleLrg}
          >
            <div dangerouslySetInnerHTML={{ __html: pageLink.title }}></div>
          </Text>
          <Text className={styles.pageLinkSummary} size={TextSizes.bodyMed}>
            {pageLink.summary}
          </Text>
        </div>
        {pageLink.links && pageLink.links.length > 0 && (
          <div className={styles.ctaLinkContainer}>
            {pageLink.links.map((link: any, index: number) => (
              <Button
                key={`multigrid-item-link-${index}-${link._key}`}
                variant={ButtonVariants.capsUnderlineSml}
                button={{
                  type: ButtonTypes.button,
                }}
                scrollToId={link?.scrollToId}
                launchForm={link?.launchForm}
                link={link.link && resolveLink(link.link)}
                iconLeft
                className={styles.ctaLink}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: `${link.linkText}` || 'Read more',
                  }}
                />
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface MultiInternalPageLinksProps {
  title: string
  intro?: Block[]
  pageLinks: any[]
  theme?: ComponentTheme
  verticalPadding?: 'none' | 'small' | 'medium' | 'large'
  backgroundVariant?: 'light' | 'dark'
  moduleId?: string
  evenSizing?: boolean
  evenSizingBackground?: boolean
  noBackground?: boolean
  noPaddingBottom?: boolean
  noMobileSlider?: boolean
}

export default function MultiInternalPageLinks({
  title,
  intro,
  pageLinks,
  theme,
  verticalPadding = 'medium',
  backgroundVariant = 'light',
  moduleId,
  evenSizing = false,
  evenSizingBackground = false,
  noBackground = false,
  noPaddingBottom = false,
  noMobileSlider,
}: MultiInternalPageLinksProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)

  // Determine if we should show 3 across
  const shouldShow3Across = evenSizing && pageLinks.length === 3

  const renderDesktop = () => {
    return (
      <div
        className={classNames(styles.pageLinks, {
          [styles.evenSizing3Across]: shouldShow3Across,
        })}
      >
        {pageLinks.map((pageLink, index) => (
          <PageLinkItem
            pageLink={pageLink}
            isLarge={!evenSizing && index < 2}
            whiteCard={evenSizing && evenSizingBackground}
            key={`${pageLink._ref}-${index}`}
          />
        ))}
      </div>
    )
  }

  const renderMobile = () => {
    // If noMobileSlider is true, render all items stacked vertically
    if (noMobileSlider) {
      return (
        <div className={classNames(styles.pageLinks, styles.mobileNoSlider)}>
          {pageLinks.map((pageLink, index) => (
            <PageLinkItem
              pageLink={pageLink}
              isLarge={!evenSizing && index < 2}
              whiteCard={evenSizing && evenSizingBackground}
              key={`${pageLink._ref}-${index}`}
            />
          ))}
        </div>
      )
    }

    // Otherwise, use the original slider logic
    return (
      <div>
        {!evenSizing && (
          <div className={styles.pageLinks}>
            {pageLinks.slice(0, 2).map((pageLink, index) => (
              <PageLinkItem
                pageLink={pageLink}
                isLarge={true}
                key={`${pageLink._ref}-static-${index}`}
              />
            ))}
          </div>
        )}
        <DynamicSlider
          slidesPerView={1.2}
          overflowVisible={false}
          spaceBetween={0}
          spacer={false}
          title=""
          wrapperClass={styles.sliderWrapperClass}
          hideArrowsMobile
          customControls={styles.customSliderControls}
          useScrollbar
          theme={undefined}
        >
          {(evenSizing ? pageLinks : pageLinks.slice(2)).map(
            (pageLink, index) => (
              <PageLinkItem
                pageLink={pageLink}
                isLarge={false}
                whiteCard={evenSizing && evenSizingBackground}
                key={`${pageLink._ref}-slider-${index}`}
              />
            )
          )}
        </DynamicSlider>
      </div>
    )
  }

  return (
    <Fixture
      verticalPadding={verticalPadding !== 'none'}
      noPaddingBottom={noPaddingBottom}
      theme={theme}
      id={moduleId}
      className={classNames(styles.base, styles[backgroundVariant], {
        [styles.noBackground]: noBackground,
      })}
    >
      <Container>
        <div className={styles.content}>
          {(title || intro) && (
            <div className={styles.introContainer}>
              {title && (
                <Text className={styles.title} size={TextSizes.primaryTitleLrg}>
                  <div dangerouslySetInnerHTML={{ __html: title }}></div>
                </Text>
              )}
              {intro && (
                <div className={classNames(styles.summary, 'prose')}>
                  <BlockContent value={intro} />
                </div>
              )}
            </div>
          )}
          {isMobile ? renderMobile() : renderDesktop()}
        </div>
      </Container>
    </Fixture>
  )
}
